// CRM-Datenschicht: Kunde + Adresse + Korb als EINE Einheit.
//
// Der Bauer denkt nicht in Tabellen — er denkt "der Kunde in der Wichernstraße 24
// kriegt dienstags 20 braune Eier". Deshalb liefern/schreiben die Hooks hier alles
// zusammen, statt drei Screens für drei Tabellen zu bauen.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Customer, CustomerAddress, CustomerType, InterestTag } from "@/lib/db";
// Token synchron aus dem Zustand — supabase.auth.getSession() hängt im Funkloch
// (siehe lib/tour.ts), und diese Mutation soll nicht daran steckenbleiben.
import { useAuth } from "@/stores/authStore";

export interface BasketItem {
  product_id: string;
  qty: number;
}

export interface BasketTemplate {
  id: string;
  name: string;
  items: (BasketItem & { sku?: string })[];
  use_count: number;
  sort_order: number;
}

export interface CustomerFull extends Customer {
  addresses: CustomerAddress[];
  subscriptions: {
    id: string;
    weekdays: number[];
    interval_weeks: number;
    is_active: boolean;
    items: { id: string; product_id: string; qty: number }[];
  }[];
}

/** Kundenliste — bewusst schlank, die Liste hat 225 Zeilen. */
/** Kunde in der Liste — inkl. der Wochentage, an denen er beliefert wird. */
export type CustomerListItem = Customer & {
  addresses: Pick<CustomerAddress, "street" | "city" | "geo_lat">[];
  subscriptions: { weekdays: number[]; is_active: boolean }[];
};

/** Liefertage eines Kunden (1=Mo … 6=Sa) aus seinen aktiven Abos. */
export function lieferTage(c: Pick<CustomerListItem, "subscriptions">): number[] {
  const tage = new Set<number>();
  for (const s of c.subscriptions ?? []) {
    if (!s.is_active) continue;
    for (const d of s.weekdays ?? []) tage.add(d);
  }
  return [...tage].sort();
}

const LIST_SELECT = `id, customer_number, display_name, notes, interest_tags, preferred_time,
   is_active, legacy_seq,
   addresses:customer_addresses(street, city, geo_lat),
   subscriptions:subscriptions(weekdays, is_active)`;

/**
 * Die Kundenliste — wahlweise auf einen Liefertag eingegrenzt.
 *
 * ══ Warum der Tag hier hin gehört und nicht in die Seite ══
 *
 * Bis zum 23.07. holte diese Abfrage die ersten 400 Kunden und die SEITE
 * filterte danach im Browser nach Wochentag. Solange der Hof 223 Kunden hatte,
 * fiel das nicht auf. Mit 687 war die Grenze überschritten: Die 85 frisch
 * angelegten Mittwochskunden tragen die laufenden Nummern 1115 ff., landeten
 * damit hinter Platz 400 — und wurden nie geladen. Wer auf „Mi" tippte, sah
 * eine leere Liste und musste glauben, es gebe keine Mittwochskunden.
 *
 * Ein Filter, der nur auf dem anwendet, was zufällig schon geladen ist, ist
 * keiner. Deshalb grenzt jetzt die Datenbank ein: `!inner` wirft jeden Kunden
 * raus, der am gewählten Tag kein aktives Abo hat, und das Limit greift erst
 * DANACH.
 *
 * @param tage 1=Mo … 6=Sa. Leer heisst: alle Kunden.
 */
export function useCustomerList(search: string, tage: number[] = []) {
  const tagListe = [...tage].sort().join(",");
  return useQuery({
    queryKey: ["crm-customers", search, tagListe],
    queryFn: async () => {
      // Ein Kunde kann mehrere Abos haben. `!inner` verlangt mindestens eines,
      // das passt — sonst kommt der Kunde gar nicht erst mit.
      const select = tage.length
        ? LIST_SELECT.replace("subscriptions:subscriptions(", "subscriptions:subscriptions!inner(")
        : LIST_SELECT;

      const s0 = search.trim();
      // Bei einer Suche bleiben stillgelegte Kunden absichtlich drin — wer
      // gezielt nach einem Namen sucht, will ihn auch dann finden.
      let q = supabase
        .from("customers")
        .select(select)
        .order("legacy_seq", { nullsFirst: false })
        .limit(1000);
      if (s0.length < 2) q = q.eq("is_active", true);

      if (tage.length) {
        q = q.eq("subscriptions.is_active", true);
        // Mehrfachauswahl ist ein ODER: „Mi oder Fr". `cs` heisst „enthält" —
        // weekdays ist eine Liste, ein Kunde kann an mehreren Tagen beliefert
        // werden.
        q = q.or(tage.map((d) => `weekdays.cs.{${d}}`).join(","), {
          referencedTable: "subscriptions",
        });
      }

      if (s0.length >= 2) {
        // Der Suchbegriff geht ungeschützt in einen PostgREST-.or()-Filter — Komma
        // und Klammern würden die Filtersyntax aufbrechen (500er oder fremde
        // Bedingung). Deshalb entfernen wir alles, was PostgREST als Syntax liest.
        //
        // Der Filter wird auf die BESTEHENDE Abfrage gelegt, nicht auf eine neue:
        // Vorher baute dieser Zweig `q` komplett neu — und warf damit den
        // Tagesfilter weg. Wer „Mi" gewählt hatte und dann suchte, bekam
        // Treffer von allen Wochentagen.
        const safe = s0.replace(/[(),*"\\]/g, " ").trim();
        if (!safe) return [];
        const orParts = [`display_name.ilike.%${safe}%`, `customer_number.ilike.%${safe}%`];
        // Adresse steht in einer eigenen Tabelle. Ein PostgREST-.or() kann nicht
        // gleichzeitig über Kunde UND Adresse gehen — also holen wir die
        // passenden Kunden-IDs vorab und hängen sie als drittes ODER an.
        const { data: addrHits } = await supabase
          .from("customer_addresses")
          .select("customer_id")
          .or(`street.ilike.%${safe}%,city.ilike.%${safe}%,zip.ilike.%${safe}%`)
          .limit(1000);
        const ids = [...new Set((addrHits ?? []).map((a) => a.customer_id).filter(Boolean))];
        if (ids.length) orParts.push(`id.in.(${ids.join(",")})`);
        q = q.or(orParts.join(","));
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as CustomerListItem[];
    },
  });
}

export function useCustomerFull(id: string | undefined) {
  return useQuery({
    queryKey: ["crm-customer", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select(
          `*,
           addresses:customer_addresses(*),
           subscriptions:subscriptions(id, weekdays, interval_weeks, is_active,
             items:subscription_items(id, product_id, qty))`
        )
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as unknown as CustomerFull;
    },
  });
}

export function useBasketTemplates() {
  return useQuery({
    queryKey: ["basket-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("basket_templates")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as BasketTemplate[];
    },
  });
}

export interface CustomerDraft {
  id?: string;
  display_name: string;
  street: string;
  city: string;
  zip?: string;
  notes?: string | null;
  phone?: string | null;
  email?: string | null;
  interest_tags: InterestTag[];
  preferred_time?: string | null;
  /**
   * Privat oder gewerblich.
   *
   * Der Unterschied ist nicht kosmetisch: Ein Gewerbekunde bekommt IMMER ein
   * Rechnungs-PDF, auch wenn er bar zahlt — er verbucht sie und zieht die
   * Vorsteuer. Beim Privatkunden entsteht das PDF nur auf Nachfrage.
   */
  type?: CustomerType;
  /** Korb + Turnus */
  items: BasketItem[];
  weekdays: number[];
  interval_weeks: number;
}

/**
 * Legt Kunde + Adresse + Abo in einem Rutsch an (oder aktualisiert sie).
 * Geocoding läuft danach im Hintergrund — ohne Koordinaten fällt der Kunde
 * sonst aus dem Routenbau, aber der Nutzer soll darauf nicht warten müssen.
 */
export function useSaveCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (d: CustomerDraft) => {
      /**
       * Konnten die Koordinaten NICHT ermittelt werden? Dann ist der Kunde zwar
       * gespeichert, fiele aber aus jeder Route — das muss die Oberfläche sagen
       * können, statt es zu verschweigen.
       */
      let geoFehler: string | null = null;

      // 1) Kunde
      const custPayload = {
        ...(d.id ? { id: d.id } : {}),
        type: d.type ?? "privat",
        display_name: d.display_name.trim() || d.street.trim(),
        notes: d.notes ?? null,
        phone: d.phone ?? null,
        email: d.email ?? null,
        interest_tags: d.interest_tags,
        preferred_time: d.preferred_time || null,
        is_active: true,
      };
      const { data: cust, error: cErr } = await supabase
        .from("customers")
        .upsert(custPayload, { onConflict: "id" })
        .select()
        .single();
      if (cErr) throw cErr;
      const customerId = (cust as Customer).id;

      // 2) Adresse (Default-Adresse des Kunden)
      const { data: existingAddr } = await supabase
        .from("customer_addresses")
        .select("id")
        .eq("customer_id", customerId)
        .eq("is_default", true)
        .maybeSingle();

      const addrPayload = {
        ...(existingAddr ? { id: existingAddr.id } : {}),
        customer_id: customerId,
        label: "Lieferadresse",
        street: d.street.trim(),
        zip: d.zip?.trim() || "33330",
        city: d.city.trim() || "Gütersloh",
        country: "DE",
        delivery_notes: d.notes ?? null,
        is_default: true,
      };
      const { data: addr, error: aErr } = await supabase
        .from("customer_addresses")
        .upsert(addrPayload, { onConflict: "id" })
        .select()
        .single();
      if (aErr) throw aErr;

      // 3) Abo (Korb + Turnus)
      // `is_active` und `starts_on` werden mitgelesen, weil beide über den
      // Turnus entscheiden — siehe subPayload weiter unten.
      const { data: existingSub } = await supabase
        .from("subscriptions")
        .select("id, is_active, starts_on")
        .eq("customer_id", customerId)
        .maybeSingle();

      // ══ Was heißt "bekommt keine Lieferungen mehr"? ══
      //
      // Bis zum 22.07.: ein leerer Korb. Das galt, solange ein Kunde ohne Ware
      // sinnlos war.
      //
      // Seitdem gilt (Marvin): „Es muss auch kein Produkt im Korb angelegt sein.
      // Wenn man einen Tag und den Turnus auswählt, muss er in der Tour
      // erscheinen." Der Liefertag ist jetzt das Merkmal, nicht der Korb — was
      // jemand bekommt, kann an der Haustür entschieden werden.
      //
      // Abbestellt ist damit, wer KEINEN Liefertag mehr hat. Ohne diese
      // Umstellung hätte das Abschalten eines Abos gar keinen Weg mehr: Man
      // leert den Korb, der Kunde bleibt auf der Tour, und niemand versteht,
      // warum der Fahrer weiter klingelt.
      const keinLiefertag = d.weekdays.length === 0;

      if (keinLiefertag) {
        if (existingSub) {
          const { error } = await supabase
            .from("subscriptions")
            .update({ is_active: false, ends_on: new Date().toISOString().slice(0, 10) })
            .eq("id", existingSub.id);
          if (error) throw error;
        }
      } else {
        // ══ starts_on ist der Taktgeber — er darf beim Ändern NICHT wandern ══
        //
        // Der Cron rechnet den Turnus ausschließlich von hier aus:
        //   floor(Tage(starts_on → Liefertag) / 7) % interval_weeks === 0
        //
        // Stand hier vorher unbedingt auf "heute" — also auch beim ÄNDERN eines
        // bestehenden Abos. Damit verschob jede Korb-Korrektur den Rhythmus:
        // Ein 14-tägiger Kunde, dessen Eierzahl am Mittwoch angepasst wurde,
        // war am folgenden Dienstag wieder dran (floor(6/7)=0, 0 % 2 === 0) —
        // in seiner Aus-Woche, mit echter Ware und echter Rechnung.
        //
        // Nur zwei Fälle setzen den Anker neu: ein ganz neues Abo, und ein
        // reaktiviertes (der Korb war leer, das Abo abgeschaltet — dann ist der
        // alte Takt hinfällig). Ein laufendes Abo behält seinen Anker.
        const laeuftBereits = !!existingSub?.is_active;
        const subPayload = {
          ...(existingSub ? { id: existingSub.id } : {}),
          customer_id: customerId,
          address_id: (addr as CustomerAddress).id,
          weekdays: d.weekdays,
          interval_weeks: d.interval_weeks,
          starts_on: laeuftBereits
            ? existingSub!.starts_on
            : new Date().toISOString().slice(0, 10),
          ends_on: null,
          payment_method: "cash" as const,
          is_active: true,
        };
        const { data: sub, error: sErr } = await supabase
          .from("subscriptions")
          .upsert(subPayload, { onConflict: "id" })
          .select()
          .single();
        if (sErr) throw sErr;

        // Items komplett ersetzen — einfacher und korrekter als Diffing.
        // Ein leerer Korb ist erlaubt: Der Kunde wird trotzdem angefahren.
        await supabase.from("subscription_items").delete().eq("subscription_id", sub.id);
        if (d.items.length) {
          const { error: iErr } = await supabase.from("subscription_items").insert(
            d.items.map((i) => ({
              subscription_id: sub.id,
              product_id: i.product_id,
              qty: i.qty,
            }))
          );
          if (iErr) throw iErr;
        }
      }

      // 4) Koordinaten holen und an der Adresse festschreiben.
      //
      // MIT PLZ. Solange jeder Kunde in Gütersloh lag, reichte "Straße, Ort".
      // Seit der Ort frei wählbar ist, wird die Adresse mehrdeutig — "Hauptstraße 1"
      // gibt es in jedem zweiten Ort. Ohne PLZ setzt der Geocoder den Kunden dann
      // irgendwohin, und die Route fährt in die falsche Stadt.
      //
      // ══ Warum hier GEWARTET wird ══
      // Das lief früher als fire-and-forget mit einem leeren `.catch()`. Damit
      // war der Aufruf doppelt unsichtbar: Er ging an die falsche Stelle (der
      // Handler las nur ?q=, hier ging ein Body raus) und antwortete mit 400 —
      // aber niemand sah es je. Ergebnis: JEDER über die App angelegte Kunde
      // blieb ohne Koordinaten und wurde beim Tourbau stillschweigend
      // übersprungen.
      //
      // Der Aufruf dauert unter einer Sekunde und ist der Unterschied zwischen
      // "steht auf der Tour" und "existiert nur in der Liste". Scheitert er,
      // bleibt der Kunde trotzdem gespeichert — aber der Fehler wird
      // durchgereicht, damit die Oberfläche ihn zeigen kann.
      const query = `${addrPayload.street}, ${addrPayload.zip} ${addrPayload.city}, Deutschland`;
      const addressId = (addr as CustomerAddress).id;
      try {
        const r = await fetch("/api/geocode", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ query, address_id: addressId }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(
            j?.error === "not_found"
              ? `Die Adresse „${addrPayload.street}, ${addrPayload.zip} ${addrPayload.city}" wurde nicht gefunden. Der Kunde ist gespeichert, steht aber ohne Korrektur auf keiner Tour.`
              : `Die Adresse konnte nicht auf der Karte gefunden werden (${j?.error ?? r.status}). Der Kunde ist gespeichert, steht aber ohne Korrektur auf keiner Tour.`
          );
        }
      } catch (e) {
        // Der Kunde IST gespeichert — deshalb kein throw, das den Datensatz
        // infrage stellt, sondern eine Meldung, die zum Nachbessern auffordert.
        geoFehler = (e as Error).message;
      }

      // 5) Sofort einplanen — der Kunde muss JETZT auf den Touren stehen.
      //
      // Vorher entstand aus einem Abo erst im nächtlichen Lauf um 4 Uhr eine
      // Bestellung und daraus die Tour. Wer abends einen Kunden für morgen
      // anlegte, sah ihn auf der Tour nicht und hatte keinen Grund anzunehmen,
      // dass da noch etwas kommt. Genau so standen am 22.07. zwei Kunden für
      // Donnerstag im System und auf keiner Tour.
      //
      // NACH dem Geocoding, nicht davor: Ohne Koordinaten fiele der Kunde beim
      // Routenbau wieder heraus.
      //
      // Scheitert es, ist der Kunde trotzdem gespeichert — der Nachtlauf holt
      // es dann nach. Deshalb eine Meldung statt eines throw; aber eben eine
      // Meldung und kein stilles Schlucken.
      let planFehler: string | null = null;
      try {
        const r = await fetch("/api/tour?action=abo-einplanen", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${useAuth.getState().session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ customerId }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j?.error ?? `HTTP ${r.status}`);
      } catch (e) {
        planFehler = `Der Kunde ist gespeichert, konnte aber nicht sofort auf die Touren gesetzt werden (${
          (e as Error).message
        }). Der nächtliche Lauf holt das nach.`;
      }

      return { ...(cust as Customer), geoFehler, planFehler };
    },

    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["crm-customers"] });
      qc.invalidateQueries({ queryKey: ["crm-customer", c.id] });
      // Die Touren haben sich gerade geändert — sonst zeigt die Tour-Seite den
      // Stand von vor dem Speichern und man hält das Einplanen für kaputt.
      qc.invalidateQueries({ queryKey: ["tour"] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}
